import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import * as z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CLASSIFICATION } from 'shared/constants/organization.constants';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { updateOrganization } from '@/api/organization.api.private';
import { useToast } from '@/components/ui/use-toast';
import { useOrganizationContext } from '../organization.context';

const domainRegex = /^(?!:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;

const ORGANIZATION_DETAILS_SCHEMA = z.object({
  name: z.string().min(2, {
    message: 'Name must be at least 2 characters.',
  }),
  // logo: z.string(),
  domain: z.array(
    z.string().regex(domainRegex, {
      message: 'Invalid domain format',
    }),
  ),
  classification: z.enum(Object.values(CLASSIFICATION)),
});

export default function DetailsBox() {
  const { setOrganization, organization } = useOrganizationContext();
  const [newDomain, setNewDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const form = useForm({
    resolver: zodResolver(ORGANIZATION_DETAILS_SCHEMA),
    defaultValues: organization,
  });

  const addDomain = e => {
    if (newDomain && e.key === 'Enter') {
      e.preventDefault();
      try {
        // check if domain is valid
        z.string()
          // using regex from zod4 regexes.domain: https://zod.dev/api#string-formats
          .regex(/^([a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/)
          .parse(newDomain);

        const domains = form.getValues('domain');

        if (domains.includes(newDomain)) {
          form.setError('domain', {
            message: 'Domain already exists',
          });
        } else {
          form.setValue('domain', [...domains, newDomain]);
          setNewDomain('');
        }
      } catch (e) {
        form.setError('domain', {
          message: 'Domain should be like example.com',
        });
      }
    }
  };

  const onSubmit = async formData => {
    setLoading(true);
    const { data, error } = await updateOrganization({
      data: formData,
      id: organization.id,
    });

    setLoading(false);

    if (error) {
      toast({ status: 'error', title: data.message, variant: 'destructive' });
      return;
    }

    setOrganization(oldOrganization => ({ ...oldOrganization, ...data }));

    toast({
      status: 'success',
      title: `Organization ${data.name} updated successfully`,
    });

    form.reset(data);
  };

  const handleDomainChange = e => {
    form.clearErrors('domain');
    setNewDomain(e.target.value);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Details</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            id="organization"
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6 w-full">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">Name</FormLabel>
                  <FormControl>
                    <Input placeholder="C8 Health" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="classification"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">Classification</FormLabel>
                  <FormControl>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.values(CLASSIFICATION).map(value => (
                          <SelectItem key={value} value={value}>
                            {value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Separator />
            <div className="flex flex-row gap-10">
              <div className="flex flex-col gap-5 flex-1">
                <FormField
                  control={form.control}
                  name="domain"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-bold">Allowed Domains</FormLabel>
                      <FormControl>
                        <Input
                          value={newDomain}
                          onChange={handleDomainChange}
                          onKeyDown={addDomain}
                        />
                      </FormControl>
                      <FormMessage />

                      <div className="flex-grow-0">
                        {field.value?.length > 0 ? (
                          field.value.map((domain, index) => (
                            <Badge
                              key={index}
                              variant="secondary"
                              className="p-2 mr-2 my-2 hover:bg-primary/10">
                              {domain}
                              <X
                                className="cursor-pointer"
                                size={16}
                                onClick={e => {
                                  e.preventDefault();
                                  field.onChange(field.value.filter(d => d !== domain));
                                }}
                              />
                            </Badge>
                          ))
                        ) : (
                          <FormDescription className="h-10">No domain added yet</FormDescription>
                        )}
                      </div>
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Button type="submit" loading={loading} disabled={loading}>
              Submit
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
